import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { isNil } from 'lodash-es'
import { Locale, Translation } from 'locale'
import { CollateralBasketDetail, DealDetail, DealStatus, OrderDetail, ServicingType, ServicingTypeNewDeal } from 'api'
import {
  AssignableNameCellRenderer,
  axeTradeConditionKeyCreator,
  createActionsRenderer,
  createAxeTradeConditionRenderer,
  createGroupedActionRenderer,
  dateFilterParams,
  getBasketDealIdText,
  GridId,
  gridOptionsBase,
  isSelectedForRelatedDealTrades,
  StandardGridColumnId,
  StatusCellRenderer,
  tradesGridGroupingRowColDef,
  TypedValueGetterParams,
  ValidationCellRenderer,
  withAllContext
} from 'view/components/datagrid'
import {
  axeFilterValueFormatter,
  axeTradeConditionFilterTextGetter,
  booleanFormatter,
  callableTypeFormatter,
  collectionFilterFormatter,
  collectionFormatter,
  collectionKeyCreator,
  dateQuickFilterTextGetter,
  evergreenNoticeFormatter,
  getClearingStatus,
  getExecutionDecisionMakerName,
  getInvestmentDecisionMakerName,
  getOwnDirection,
  getSettlementVenueName,
  GridAxeTradeConditionButtons,
  haircutTypeFormatter,
  makeBooleanFormatter,
  makeBooleanQuickFilterTextGetter,
  makeCusipGetter,
  makeDateFormatter,
  makeExecutingTradingEntityNameGetter,
  makeFeeFormatter,
  makeHaircutFilterGetter,
  makeHaircutFormatter,
  makeHaircutQuickFilterTextGetter,
  makeIsinGetter,
  makeNotionalFilterGetter,
  makeNotionalFormatter,
  makeNumberFilterGetter,
  makeNumberFormatter,
  makeNumberQuickFilterTextGetter,
  makeOrganisationFieldGetter,
  makeRateFilterGetter,
  makeRateFormatter,
  makeRateQuickFilterTextGetter,
  makeSponsorNameGetter,
  makeTimeFormatter,
  makeTradingEntityFieldGetter,
  makeTradingEntityLeiGetter,
  makeTradingEntityNameGetter,
  NOTE_INDEXES,
  notionalHeaderValueGetter,
  orderTermFormatter,
  orderTermQuickFilterTextGetter,
  repoClassificationFormatter,
  safeValueGetter,
  servicingTypeFormatter,
  servicingTypeQuickFilterTextGetter,
  timeQuickFilterTextGetter
} from 'view/shared/datagrid'
import { DealActionsButton } from './DealActionsButton'
import { DealsGridColumnId, DealFieldNames } from './DealsGridColumnNames'
import { makeTraderGetter, NAFormatter } from './dealsFormatters'
import { basketDealIdComparator, getDealStatusColor, getDealStatusText } from './utils'
import { ClearingGridColumnId, CollateralBasketColumnId, CollectionColumnId, ContainerCollectionColumnId } from 'view/routes/ColumnNames'
import { AllocationTimeRenderer } from './AllocationTimeRenderer'
import { MarketHelper, UserHelper } from 'model'
import { OpenTicketActionsButton } from './OpenTicketActionsButton'
import { getDealIndexRatio } from 'model/dealUtils'
import {
  BASE_ASSET_TYPE,
  BASE_COLLECTION_TYPE,
  BASE_DIRECTION,
  BASE_ISSUE,
  BASE_MARKET_TYPE,
  BASE_REPO_TYPE,
  BASE_ROE,
  BASE_ROS
} from 'view/shared/datagrid/baseColumns'

const CHECKBOX: ColDef = {
  colId: StandardGridColumnId.CHECKBOX,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  headerCheckboxSelection: true,
  headerCheckboxSelectionFilteredOnly: true,
  checkboxSelection: true,
  width: 36,
  maxWidth: 36,
  pinned: 'left',
  lockPinned: true,
  enableRowGroup: false,
  lockPosition: true,
  lockVisible: true,
  filter: false,
  resizable: false,
  sortable: false
}

const ACTIONS: ColDef = {
  colId: StandardGridColumnId.ACTIONS,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  equals: () => false,
  width: 180,
  minWidth: 180,
  headerComponent: withAllContext(createGroupedActionRenderer(DealActionsButton)),
  cellClass: 'action-cell',
  cellRenderer: withAllContext(createActionsRenderer(DealActionsButton)),
  pinned: 'left',
  lockPinned: true,
  enableRowGroup: false,
  lockVisible: true,
  filter: false,
  sortable: false
}

const DEAL_STATUS: ColDef = {
  colId: DealsGridColumnId.DEAL_STATUS,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    params.data && getDealStatusText(params.data, params.context.environmentContext.isAdmin),
  headerName: Locale.translate(Translation.TC_STATUS),
  width: 100,
  filter: 'agSetColumnFilter',
  cellRenderer: StatusCellRenderer,
  cellRendererParams: {
    getStatusColor: getDealStatusColor
  },
  lockVisible: true
}

const COUNTERPARTY_NAME: ColDef = {
  colId: DealsGridColumnId.COUNTERPARTY_NAME,
  valueGetter: makeTradingEntityNameGetter(false),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY),
  width: 150,
  filter: 'agSetColumnFilter'
}

const COLLECTION_NAME: ColDef = {
  colId: CollectionColumnId.COLLECTION_NAME,
  headerName: Locale.translate(Translation.TC_COLLECTION_NAME),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.collection,
  keyCreator: collectionKeyCreator,
  valueFormatter: collectionFormatter,
  cellRenderer: AssignableNameCellRenderer,
  cellRendererParams: {
    unassignedLabel: Locale.translate(Translation.TC_COLLECTION_UNASSIGNED)
  },
  width: 150,
  filter: 'agSetColumnFilter',
  filterParams: {
    valueFormatter: collectionFilterFormatter
  }
}

const DEAL_FACTOR: ColDef = {
  colId: DealsGridColumnId.FACTOR,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.deal?.factor,
  headerName: Locale.translate(Translation.TC_FACTOR),
  width: 150,
  filter: 'agSetColumnFilter'
}

const DEAL_CREATED_DUE_TO_SERVICING_TYPE: ColDef = {
  colId: DealsGridColumnId.DEAL_CREATED_DUE_TO_SERVICING_TYPE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
    const createdDueToServicingType = params?.data?.deal?.createdDueToServicingType
    const committedServicingType = params?.data?.deal?.committedServicingType

    if (params?.data?.deal?.status === DealStatus.CANCELLED) {
      return null
    }

    if (!committedServicingType && !createdDueToServicingType) {
      return ServicingTypeNewDeal
    }

    return params?.data?.deal?.createdDueToServicingType
  },
  valueFormatter: servicingTypeFormatter,
  getQuickFilterText: servicingTypeQuickFilterTextGetter,
  headerName: Locale.translate(Translation.TC_ORIGINATING_EVENT),
  width: 150,
  filter: 'agSetColumnFilter',
  filterParams: { valueFormatter: servicingTypeFormatter }
}

const DEAL_COMMITTED_SERVICING_TYPE: ColDef = {
  colId: DealsGridColumnId.DEAL_COMMITTED_SERVICING_TYPE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
    if (params?.data?.deal?.status === DealStatus.CANCELLED) {
      return ServicingType.SERVICING_TRADER_CANCEL
    }

    return params?.data?.deal?.committedServicingType ?? ServicingTypeNewDeal
  },
  valueFormatter: servicingTypeFormatter,
  getQuickFilterText: servicingTypeQuickFilterTextGetter,
  headerName: Locale.translate(Translation.TC_EVENT),
  width: 150,
  filter: 'agSetColumnFilter',
  filterParams: {
    valueFormatter: servicingTypeFormatter
  }
}

const DEAL_ASSET_TYPE: ColDef = {
  ...BASE_ASSET_TYPE,
  colId: DealsGridColumnId.ASSET_TYPE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.deal?.assetType,
  width: 150
}

const CONTAINER_COLLECTION_ID: ColDef = {
  colId: ContainerCollectionColumnId.CONTAINER_COLLECTION_ID,
  headerName: Locale.translate(Translation.TC_CONTAINER_COLLECTION_ID),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.containerCollection?.id,
  width: 150,
  filter: 'agSetColumnFilter'
}

const CONTAINER_COLLECTION_NAME: ColDef = {
  colId: ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME,
  headerName: Locale.translate(Translation.TC_CONTAINER_COLLECTION_NAME),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.containerCollection?.name,
  width: 150,
  filter: 'agSetColumnFilter'
}

const COLLECTION_TYPE: ColDef = { ...BASE_COLLECTION_TYPE }

const COLLATERAL_ISIN_OR_BUI: ColDef = {
  colId: DealsGridColumnId.COLLATERAL_ISIN_OR_BUI,
  valueGetter: makeIsinGetter(),
  headerName: Locale.translate(Translation.TC_ISIN_BUI),
  width: 140,
  filter: 'agSetColumnFilter'
}

const COLLATERAL_NAME: ColDef = {
  colId: DealsGridColumnId.COLLATERAL_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.collateral?.name ?? params.data?.collateralBasket?.name,
  headerName: Locale.translate(Translation.TC_COLLATERAL),
  width: 120,
  filter: 'agSetColumnFilter'
}

const COLLATERAL_BASKET_NAME: ColDef = {
  colId: DealsGridColumnId.COLLATERAL_BASKET_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.collateralBasketName,
  headerName: Locale.translate(Translation.TC_BASKET_NAME),
  width: 120,
  filter: 'agSetColumnFilter'
}

const SECONDARY_COLLATERAL_NAME: ColDef = {
  colId: DealsGridColumnId.SECONDARY_COLLATERAL_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.secondaryInstrumentName,
  headerName: Locale.translate(Translation.TC_SECONDARY_COLLATERAL),
  width: 120,
  filter: 'agSetColumnFilter'
}

const COLLATERAL_BASKET_REMAINING_ALLOCATION_TIME: ColDef = {
  colId: CollateralBasketColumnId.COLLATERAL_BASKET_REMAINING_ALLOCATION_TIME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.basketAllocation?.endTime,
  cellRenderer: AllocationTimeRenderer,
  equals: () => false,
  headerName: Locale.translate(Translation.TC_ALLOCATION_TIME),
  width: 110
}

const COLLATERAL_BASKET_DESCRIPTION: ColDef = {
  colId: CollateralBasketColumnId.COLLATERAL_BASKET_DESCRIPTION,
  headerName: Locale.translate(Translation.TC_BASKET_DESCRIPTION),
  valueGetter: (params: TypedValueGetterParams<OrderDetail>) => params.data?.collateralBasket?.description,
  width: 220,
  filter: 'agSetColumnFilter'
}

const COLLATERAL_BASKET_DEAL_ID: ColDef = {
  colId: CollateralBasketColumnId.COLLATERAL_BASKET_DEAL_ID,
  headerName: Locale.translate(Translation.TC_BASKET_DEAL_ID),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => getBasketDealIdText(params.data?.deal),
  cellRenderer: AssignableNameCellRenderer,
  cellRendererParams: {
    unassignedLabel: Locale.translate(Translation.TC_SINGLE_TRADES),
    hideUnassignedLabel: true
  },
  width: 130,
  filter: 'agSetColumnFilter',
  comparator: basketDealIdComparator
}

const COLLATERAL_BASKET_DBV_CLASS: ColDef = {
  colId: CollateralBasketColumnId.COLLATERAL_BASKET_DBV_CLASS,
  headerName: Locale.translate(Translation.TC_DBV_CLASS),
  valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket?.dbvClass,
  width: 120,
  filter: 'agSetColumnFilter'
}

const DEAL_DIRECTION: ColDef = {
  ...BASE_DIRECTION,
  colId: DealsGridColumnId.DEAL_DIRECTION,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    params.data && getOwnDirection(params.data.deal.direction, params.data.ownOrder),
  width: 100
}

const DEAL_NOTIONAL: ColDef = {
  colId: DealsGridColumnId.DEAL_NOTIONAL,
  headerValueGetter: notionalHeaderValueGetter,
  headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.notional,
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNotionalFormatter(),
  filterValueGetter: makeNotionalFilterGetter(),
  enableRowGroup: false
}

const DEAL_CLEAN_PRICE: ColDef = {
  colId: DealsGridColumnId.DEAL_CLEAN_PRICE,
  headerName: Locale.translate(Translation.TC_CLEAN_PRICE),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.deal?.cleanPrice,
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  filterValueGetter: makeNumberFilterGetter(),
  enableRowGroup: false
}

const DEAL_INDEX_RATIO: ColDef = {
  colId: DealsGridColumnId.DEAL_INDEX_RATIO,
  headerName: Locale.translate(Translation.TC_INDEX_RATIO),
  valueGetter: safeValueGetter(getDealIndexRatio),
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  filterValueGetter: makeNumberFilterGetter(),
  enableRowGroup: false
}

const PRODUCT: ColDef = {
  colId: DealsGridColumnId.PRODUCT,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.product?.name,
  headerName: Locale.translate(Translation.TC_PRODUCT),
  width: 80,
  filter: 'agSetColumnFilter'
}

const PRODUCT_SECTOR: ColDef = {
  colId: DealsGridColumnId.PRODUCT_SECTOR,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.productSector?.name,
  headerName: Locale.translate(Translation.TC_SECTOR),
  width: 100,
  filter: 'agSetColumnFilter'
}

const DEAL_REPO_TYPE: ColDef = {
  ...BASE_REPO_TYPE,
  colId: DealsGridColumnId.DEAL_REPO_TYPE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.repoType
}

const DEAL_STOCK_BORROWING_LENDING_TYPE: ColDef = {
  colId: DealsGridColumnId.DEAL_STOCK_BORROWING_LENDING_TYPE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.stockBorrowingLendingType,
  headerName: Locale.translate(Translation.TC_STOCK_BORROW_LENDING_TYPE),
  width: 100,
  filter: 'agSetColumnFilter'
}

const DEAL_TRADE_TYPE: ColDef = {
  colId: DealsGridColumnId.DEAL_TRADE_TYPE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.repoClassification,
  headerName: Locale.translate(Translation.TC_TRADE_TYPE),
  width: 100,
  filter: 'agSetColumnFilter',
  valueFormatter: repoClassificationFormatter
}

const REFERENCE_RATE_NAME: ColDef = {
  colId: DealsGridColumnId.REFERENCE_RATE_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.referenceRate?.name,
  headerName: Locale.translate(Translation.TC_REFERENCE_RATE),
  width: 180,
  filter: 'agSetColumnFilter'
}

const FLOAT_CRYSTALLISATION_DAYS = {
  colId: DealsGridColumnId.FLOAT_CRYSTALLISATION_DAYS,
  headerName: Locale.translate(Translation.TC_FLOAT_CRYSTALLISATION_DAYS),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.floatCrystallisationDays,
  width: 120,
  filter: 'agSetColumnFilter',
  equals: () => false,
  editable: false,
  lockPinned: true
}

const DEAL_RATE: ColDef = {
  colId: DealsGridColumnId.DEAL_RATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.rate,
  headerName: Locale.translate(Translation.TC_RATE),
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeRateFormatter(),
  filterValueGetter: makeRateFilterGetter(),
  getQuickFilterText: makeRateQuickFilterTextGetter(),
  enableRowGroup: false
}

const DEAL_FEE: ColDef = {
  colId: DealsGridColumnId.DEAL_FEE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.fee,
  headerName: Locale.translate(Translation.TC_FEE),
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeFeeFormatter(),
  enableRowGroup: false
}

const DEAL_HAIRCUT_TYPE: ColDef = {
  colId: DealsGridColumnId.DEAL_HAIRCUT_TYPE,
  headerName: Locale.translate(Translation.TC_COLLATERAL_HAIRCUT),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.haircutType,
  valueFormatter: haircutTypeFormatter,
  width: 120,
  filter: 'agSetColumnFilter'
}

const DEAL_HAIRCUT: ColDef = {
  colId: DealsGridColumnId.DEAL_HAIRCUT,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.haircut,
  headerName: Locale.translate(Translation.TC_HAIRCUT),
  equals: () => false,
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeHaircutFormatter(),
  filterValueGetter: makeHaircutFilterGetter(),
  getQuickFilterText: makeHaircutQuickFilterTextGetter(),
  enableRowGroup: false
}

const DEAL_TERM: ColDef = {
  colId: DealsGridColumnId.DEAL_TERM,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.term,
  headerName: Locale.translate(Translation.TC_TERM),
  width: 120,
  filter: 'agSetColumnFilter',
  valueFormatter: orderTermFormatter,
  getQuickFilterText: orderTermQuickFilterTextGetter,
  enableRowGroup: false
}

const DEAL_START_DATE: ColDef = {
  colId: DealsGridColumnId.DEAL_START_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.startDate,
  headerName: Locale.translate(Translation.TC_START_DATE),
  width: 120,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  valueFormatter: makeDateFormatter(),
  getQuickFilterText: dateQuickFilterTextGetter,
  enableRowGroup: false
}

const DEAL_END_DATE: ColDef = {
  colId: DealsGridColumnId.DEAL_END_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.endDate,
  valueFormatter: makeDateFormatter(),
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    rowDataGetter: (params: TypedValueGetterParams<DealDetail>) => params.data,
    validationField: DealFieldNames.DEAL_END_DATE
  },
  headerName: Locale.translate(Translation.TC_END_DATE),
  width: 120,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  getQuickFilterText: dateQuickFilterTextGetter,
  enableRowGroup: false
}

const DEAL_START_CONSIDERATION_GROUP: ColGroupDef = {
  marryChildren: true,
  children: [
    {
      colId: DealsGridColumnId.DEAL_START_CONSIDERATION,
      valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.startConsideration,
      headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
      width: 150,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      valueFormatter: makeNumberFormatter({ minDp: 2 }),
      enableRowGroup: false
    },
    {
      colId: DealsGridColumnId.DEAL_START_CONSIDERATION_OVERRIDE,
      valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.startConsiderationOverride,
      headerName: Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE),
      width: 150,
      filter: 'agSetColumnFilter',
      valueFormatter: makeBooleanFormatter(),
      getQuickFilterText: makeBooleanQuickFilterTextGetter()
    }
  ]
}

const DEAL_REPO_INTEREST: ColDef = {
  colId: DealsGridColumnId.DEAL_REPO_INTEREST,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.repoInterest,
  headerName: Locale.translate(Translation.TC_REPO_INTEREST),
  width: 100,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  enableRowGroup: false
}

const DEAL_REPO_INTEREST_START_DATE: ColDef = {
  colId: DealsGridColumnId.DEAL_REPO_INTEREST_START_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.repoInterestStartDate,
  headerName: Locale.translate(Translation.TC_REPO_INTEREST_START_DATE),
  width: 100,
  valueFormatter: makeDateFormatter(),
  filter: 'agSetColumnFilter',
  enableRowGroup: false
}

const DEAL_ACCUMULATED_REPO_INTEREST: ColDef = {
  colId: DealsGridColumnId.DEAL_ACCUMULATED_REPO_INTEREST,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
    const repoInterestToDate = params.data?.deal.repoInterestToDate

    if (repoInterestToDate === 0) return null
    return params.data?.deal.repoInterestToDate
  },
  headerName: Locale.translate(Translation.TC_PREV_REPO_INTEREST_TO_DATE),
  width: 100,
  filter: 'agSetColumnFilter',
  enableRowGroup: false
}

const DEAL_END_CONSIDERATION: ColDef = {
  colId: DealsGridColumnId.DEAL_END_CONSIDERATION,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.endConsideration,
  headerName: Locale.translate(Translation.TC_END_CONSIDERATION),
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  enableRowGroup: false
}

const FUND_NAME: ColDef = {
  colId: DealsGridColumnId.FUND_NAME,
  valueGetter: makeTradingEntityNameGetter(true),
  headerName: Locale.translate(Translation.TC_MY_ENTITY),
  width: 150,
  filter: 'agSetColumnFilter'
}

const BLOCK_TRADE: ColDef = {
  colId: DealsGridColumnId.BLOCK_TRADE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => (isNil(params.data) ? undefined : !!params.data.deal.fundAssignment),
  headerName: Locale.translate(Translation.TC_BLOCK),
  width: 80,
  filter: 'agSetColumnFilter',
  valueFormatter: makeBooleanFormatter({ defaultValue: false }),
  getQuickFilterText: makeBooleanQuickFilterTextGetter({ defaultValue: false })
}

const BLOCK_DEAL_ID: ColDef = {
  colId: DealsGridColumnId.BLOCK_DEAL_ID,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    params.data?.deal?.blockDealId ?? (params.data?.deal?.fundAssignment ? params.data?.deal.dealId : ''),
  headerName: Locale.translate(Translation.TC_BLOCK_TRADE_ID),
  width: 100,
  filter: 'agSetColumnFilter',
  enableRowGroup: false
}

const FUND_LEI: ColDef = {
  colId: DealsGridColumnId.FUND_LEI,
  valueGetter: makeTradingEntityLeiGetter(true),
  headerName: Locale.translate(Translation.TC_MY_ENTITY_LEI),
  width: 170,
  filter: 'agSetColumnFilter'
}

const FUND_BIC: ColDef = {
  colId: DealsGridColumnId.FUND_BIC,
  valueGetter: makeTradingEntityFieldGetter('bic', true),
  headerName: Locale.translate(Translation.TC_MY_ENTITY_BIC),
  width: 120,
  filter: 'agSetColumnFilter'
}

const COUNTERPARTY_CLEARING: ColDef = {
  colId: ClearingGridColumnId.COUNTERPARTY_CLEARING,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data && getClearingStatus(params.data),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_CLEARED),
  width: 70,
  filter: 'agSetColumnFilter'
}

const COUNTERPARTY_SPONSOR: ColDef = {
  colId: ClearingGridColumnId.COUNTERPARTY_SPONSOR,
  valueGetter: makeSponsorNameGetter(true),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_SPONSOR),
  width: 170,
  filter: 'agSetColumnFilter'
}

const CLEARING_VENUE: ColDef = {
  colId: ClearingGridColumnId.CLEARING_VENUE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.clearingHouse?.publishedName,
  headerName: Locale.translate(Translation.TC_CLEARING_VENUE),
  width: 150,
  filter: 'agSetColumnFilter'
}

const EXECUTING_COUNTERPARTY_NAME: ColDef = {
  colId: ClearingGridColumnId.EXECUTING_COUNTERPARTY_NAME,
  valueGetter: makeExecutingTradingEntityNameGetter(),
  headerName: Locale.translate(Translation.TC_EXECUTING_COUNTERPARTY),
  width: 170,
  filter: 'agSetColumnFilter'
}

const EXECUTING_COUNTERPARTY_SPONSOR: ColDef = {
  colId: ClearingGridColumnId.EXECUTING_COUNTERPARTY_SPONSOR,
  valueGetter: makeSponsorNameGetter(false),
  headerName: Locale.translate(Translation.TC_EXECUTING_COUNTERPARTY_SPONSOR),
  width: 210,
  filter: 'agSetColumnFilter'
}

const COUNTERPARTY_LEI: ColDef = {
  colId: DealsGridColumnId.COUNTERPARTY_LEI,
  valueGetter: makeTradingEntityLeiGetter(false),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_LEI),
  width: 150,
  filter: 'agSetColumnFilter'
}

const COUNTERPARTY_BIC: ColDef = {
  colId: DealsGridColumnId.COUNTERPARTY_BIC,
  valueGetter: makeTradingEntityFieldGetter('bic', false),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_BIC),
  width: 120,
  filter: 'agSetColumnFilter'
}

const COUNTERPARTY_TRADER: ColDef = {
  colId: DealsGridColumnId.COUNTERPARTY_TRADER,
  valueGetter: makeTraderGetter(false),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_TRADER),
  width: 150,
  filter: 'agSetColumnFilter'
}

const FUND_FIRM: ColDef = {
  colId: DealsGridColumnId.FUND_FIRM,
  valueGetter: makeOrganisationFieldGetter('legalName', true),
  headerName: Locale.translate(Translation.TC_MY_FIRM),
  width: 150,
  filter: 'agSetColumnFilter'
}

const FUND_FIRM_LEI: ColDef = {
  colId: DealsGridColumnId.FUND_FIRM_LEI,
  valueGetter: makeOrganisationFieldGetter('lei', true),
  headerName: Locale.translate(Translation.TC_MY_FIRM_LEI),
  width: 170,
  filter: 'agSetColumnFilter'
}

const COUNTERPARTY_FIRM: ColDef = {
  colId: DealsGridColumnId.COUNTERPARTY_FIRM,
  valueGetter: makeOrganisationFieldGetter('legalName', false),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM),
  width: 150,
  filter: 'agSetColumnFilter'
}

const COUNTERPARTY_FIRM_LEI: ColDef = {
  colId: DealsGridColumnId.COUNTERPARTY_FIRM_LEI,
  valueGetter: makeOrganisationFieldGetter('lei', false),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM_LEI),
  width: 150,
  filter: 'agSetColumnFilter'
}

const DEAL_SETTLEMENT_VENUE: ColDef = {
  colId: DealsGridColumnId.DEAL_SETTLEMENT_VENUE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data && getSettlementVenueName(params.data.deal.settlementVenue),
  headerName: Locale.translate(Translation.TC_SETTLEMENT),
  width: 150,
  filter: 'agSetColumnFilter'
}

const DEAL_CSD: ColDef = {
  colId: DealsGridColumnId.DEAL_CSD,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.settlement,
  headerName: Locale.translate(Translation.TC_CSD),
  width: 100,
  filter: 'agSetColumnFilter'
}

const COLLATERAL_CURRENCY: ColDef = {
  colId: DealsGridColumnId.COLLATERAL_CURRENCY,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.currency,
  headerName: Locale.translate(Translation.TC_CURRENCY),
  width: 90,
  filter: 'agSetColumnFilter'
}

const COLLATERAL_CUSIP: ColDef = {
  colId: DealsGridColumnId.COLLATERAL_CUSIP,
  valueGetter: makeCusipGetter(),
  headerName: Locale.translate(Translation.TC_CUSIP),
  width: 120,
  filter: 'agSetColumnFilter'
}

const DEAL_BOND_PRICE: ColDef = {
  colId: DealsGridColumnId.DEAL_BOND_PRICE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.bondPrice,
  headerName: Locale.translate(Translation.TC_COLLATERAL_PRICE),
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  filterValueGetter: makeNumberFilterGetter(),
  enableRowGroup: false
}

const DEAL_ALL_IN_PRICE = {
  colId: DealsGridColumnId.DEAL_ALL_IN_PRICE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.allInPrice,
  headerName: Locale.translate(Translation.TC_ALL_IN_PRICE),
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  filterValueGetter: makeNumberFilterGetter(),
  enableRowGroup: false
}

const DEAL_ACCRUED_INTEREST: ColDef = {
  colId: DealsGridColumnId.DEAL_ACCRUED_INTEREST,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.accruedInterest,
  headerName: Locale.translate(Translation.TC_COLLATERAL_ACCRUED_INTEREST),
  width: 180,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  enableRowGroup: false
}

const COLLATERAL_COUPON: ColDef = {
  colId: DealsGridColumnId.COLLATERAL_COUPON,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.collateral?.coupon,
  headerName: Locale.translate(Translation.TC_COUPON),
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ percent: true, minDp: 1, decimalShift: 2 }),
  filterValueGetter: makeNumberFilterGetter({ percent: true, minDp: 1, decimalShift: 2 }),
  getQuickFilterText: makeNumberQuickFilterTextGetter({ percent: true }),
  enableRowGroup: false
}

const COLLATERAL_ISSUER: ColDef = {
  colId: DealsGridColumnId.COLLATERAL_ISSUER,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.collateral?.issuer,
  headerName: Locale.translate(Translation.TC_ISSUER),
  width: 80,
  filter: 'agSetColumnFilter'
}

const DEAL_CALLABLE: ColDef = {
  colId: DealsGridColumnId.DEAL_CALLABLE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.callableType,
  headerName: Locale.translate(Translation.TC_CALLABLE),
  width: 100,
  filter: 'agSetColumnFilter',
  valueFormatter: callableTypeFormatter
}

const DEAL_CALL_DAYS: ColDef = {
  colId: DealsGridColumnId.DEAL_CALL_DAYS,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.callablePeriodDays,
  headerName: Locale.translate(Translation.TC_CALL_DAYS),
  width: 100,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  enableRowGroup: false
}

const DEAL_AXE_ROLL_UNWIND: ColDef = {
  colId: DealsGridColumnId.DEAL_AXE_ROLL_UNWIND,
  headerName: Locale.translate(Translation.TC_AXE_ROLL_UNWIND),
  valueGetter: ({ data }: TypedValueGetterParams<DealDetail>) => data?.axeTradeCondition,
  equals: () => false,
  cellRenderer: withAllContext(createAxeTradeConditionRenderer(GridAxeTradeConditionButtons)),
  keyCreator: axeTradeConditionKeyCreator,
  getQuickFilterText: axeTradeConditionFilterTextGetter,
  valueFormatter: axeTradeConditionFilterTextGetter,
  width: 130,
  filter: 'agSetColumnFilter',
  filterParams: { valueFormatter: axeFilterValueFormatter },
  sortable: false
}

const createBookName = (isAdmin: boolean): ColDef => ({
  colId: DealsGridColumnId.BOOK_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    isAdmin || params.data?.ownOrder ? params.data?.deal.initiatorBook : params.data?.deal.responderBook,
  headerName: Locale.translate(Translation.TC_BOOK),
  width: 180,
  filter: 'agSetColumnFilter'
})

const createStrategyName = (isAdmin: boolean): ColDef => ({
  colId: DealsGridColumnId.STRATEGY_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    isAdmin || params.data?.ownOrder ? params.data?.deal.initiatorStrategy : params.data?.deal.responderStrategy,
  headerName: Locale.translate(Translation.TC_STRATEGY),
  width: 180,
  filter: 'agSetColumnFilter'
})

const PRIME_BROKER: ColDef = {
  colId: DealsGridColumnId.PRIME_BROKER,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.initiatorPrimeBrokerName,
  headerName: Locale.translate(Translation.TC_PRIME_BROKER),
  width: 180,
  filter: 'agSetColumnFilter',
  valueFormatter: NAFormatter(DealsGridColumnId.PRIME_BROKER)
}

const BUNDLE_NAME: ColDef = {
  colId: DealsGridColumnId.BUNDLE_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.bundle,
  headerName: Locale.translate(Translation.TC_BUNDLE),
  width: 180,
  filter: 'agSetColumnFilter',
  valueFormatter: NAFormatter(DealsGridColumnId.BUNDLE_NAME)
}

const DEAL_ID: ColDef = {
  colId: DealsGridColumnId.DEAL_ID,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.dealId,
  headerName: Locale.translate(Translation.TC_DEAL_ID),
  width: 100,
  filter: 'agSetColumnFilter'
}

const DEAL_ORDER_ID: ColDef = {
  colId: DealsGridColumnId.DEAL_ORDER_ID,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.orderId,
  headerName: Locale.translate(Translation.TC_RFQ_ID),
  width: 100,
  filter: 'agSetColumnFilter',
  enableRowGroup: false
}

const DEAL_AXE_ID: ColDef<DealDetail> = {
  colId: DealsGridColumnId.DEAL_AXE_ID,
  valueGetter: params => params.data?.deal.axeId,
  headerName: Locale.translate(Translation.TC_AXE_ID),
  width: 100,
  filter: 'agSetColumnFilter',
  enableRowGroup: false
}

const DEAL_NEGOTIATION_ID: ColDef = {
  colId: DealsGridColumnId.DEAL_NEGOTIATION_ID,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.negotiationId,
  headerName: Locale.translate(Translation.TC_NEGOTIATION_ID),
  width: 100,
  filter: 'agSetColumnFilter',
  enableRowGroup: false
}

const COLLECTION_ID: ColDef = {
  colId: CollectionColumnId.COLLECTION_ID,
  headerName: Locale.translate(Translation.TC_COLLECTION_ID),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.collection?.id,
  filter: 'agSetColumnFilter',
  width: 150
}

const createUTI = (isAdmin: boolean): ColDef => ({
  colId: DealsGridColumnId.DEAL_UTI,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    isAdmin || params.data?.ownOrder ? params.data?.deal.initiatorUtiRtn : params.data?.deal.responderUtiRtn,
  headerName: Locale.translate(Translation.TC_UTI),
  width: 100,
  filter: 'agSetColumnFilter',
  enableRowGroup: false
})

const DEAL_MIC: ColDef = {
  colId: DealsGridColumnId.DEAL_MIC,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.marketIdentifierCode,
  headerName: Locale.translate(Translation.TC_MIC),
  width: 100,
  filter: 'agSetColumnFilter',
  enableRowGroup: false
}

const DEAL_ORIGINATOR: ColDef = {
  colId: DealsGridColumnId.DEAL_ORIGINATOR,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.createdBy,
  headerName: Locale.translate(Translation.TC_ORIGINATOR),
  width: 100,
  filter: 'agSetColumnFilter',
  valueFormatter: NAFormatter(DealsGridColumnId.DEAL_ORIGINATOR)
}

const TRADER: ColDef = {
  colId: DealsGridColumnId.TRADER,
  valueGetter: makeTraderGetter(true),
  headerName: Locale.translate(Translation.TC_TRADER),
  width: 100,
  filter: 'agSetColumnFilter'
}

const DEAL_RFQ_CREATED_AT_DATE: ColDef = {
  colId: DealsGridColumnId.DEAL_RFQ_CREATED_AT_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.createdAt,
  headerName: Locale.translate(Translation.TC_CREATION_DATE),
  width: 110,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  valueFormatter: makeDateFormatter(),
  getQuickFilterText: dateQuickFilterTextGetter,
  enableRowGroup: false
}

const DEAL_RFQ_CREATED_AT_TIME: ColDef = {
  colId: DealsGridColumnId.DEAL_RFQ_CREATED_AT_TIME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.createdAt,
  headerName: Locale.translate(Translation.TC_CREATION_TIME),
  width: 110,
  filter: false,
  valueFormatter: makeTimeFormatter(),
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false
}

const DEAL_TRADE_DATE: ColDef = {
  colId: DealsGridColumnId.DEAL_TRADE_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.tradedAt,
  headerName: Locale.translate(Translation.TC_DEAL_DATE),
  width: 150,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  valueFormatter: makeDateFormatter(),
  getQuickFilterText: dateQuickFilterTextGetter,
  enableRowGroup: false
}

const DEAL_TRADE_TIME: ColDef = {
  colId: DealsGridColumnId.DEAL_TRADE_TIME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.tradedAt,
  headerName: Locale.translate(Translation.TC_DEAL_TIME),
  width: 150,
  filter: false,
  valueFormatter: makeTimeFormatter(),
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false
}

const DEAL_UPDATED_BY: ColDef = {
  colId: DealsGridColumnId.DEAL_UPDATED_BY,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.lastUpdatedBy,
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
  width: 130,
  filter: 'agSetColumnFilter'
}

const DEAL_UPDATED_AT_DATE: ColDef = {
  colId: DealsGridColumnId.DEAL_UPDATED_AT_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.lastUpdatedAt,
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_DATE),
  width: 130,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  valueFormatter: makeDateFormatter(),
  getQuickFilterText: dateQuickFilterTextGetter,
  enableRowGroup: false
}

const DEAL_UPDATED_AT_TIME: ColDef = {
  colId: DealsGridColumnId.DEAL_UPDATED_AT_TIME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.lastUpdatedAt,
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_TIME),
  width: 130,
  filter: false,
  valueFormatter: makeTimeFormatter(),
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false
}

const DEAL_INVESTMENT_DECISION_MAKER: ColDef = {
  colId: DealsGridColumnId.DEAL_INVESTMENT_DECISION_MAKER,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data && getInvestmentDecisionMakerName(params.data, true),
  headerName: Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER),
  width: 180
}

const DEAL_EXECUTION_DECISION_MAKER: ColDef = {
  colId: DealsGridColumnId.DEAL_EXECUTION_DECISION_MAKER,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data && getExecutionDecisionMakerName(params.data, true),
  headerName: Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER),
  width: 180
}

const DEAL_MARKET: ColDef = {
  colId: DealsGridColumnId.DEAL_MARKET,
  headerName: Locale.translate(Translation.TC_MARKET),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
    const market = params.data?.deal.market
    return market && MarketHelper.getName(market)
  },
  width: 80,
  filter: 'agSetColumnFilter'
}

const DEAL_NOTICE_PERIOD_DAYS: ColDef = {
  colId: DealsGridColumnId.DEAL_NOTICE_PERIOD_DAYS,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.noticePeriodDays,
  headerName: Locale.translate(Translation.TC_NOTICE_PERIOD_DAYS),
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  enableRowGroup: false
}

const PARENT_DEAL_ID: ColDef = {
  colId: DealsGridColumnId.PARENT_DEAL_ID,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.previousDealId,
  headerName: Locale.translate(Translation.TC_PARENT_DEAL_ID),
  width: 120,
  filter: 'agSetColumnFilter',
  enableRowGroup: false
}

const CHILD_DEAL_ID: ColDef = {
  colId: DealsGridColumnId.CHILD_DEAL_ID,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.replacedWithDealId,
  headerName: Locale.translate(Translation.TC_CHILD_DEAL_ID),
  width: 100,
  filter: 'agSetColumnFilter',
  enableRowGroup: false
}

const ORIGINAL_DEAL_ID: ColDef = {
  colId: DealsGridColumnId.ORIGINAL_DEAL_ID,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.originalDealId,
  headerName: Locale.translate(Translation.TC_ORIGINAL_DEAL_ID),
  width: 120,
  filter: 'agSetColumnFilter',
  enableRowGroup: false
}

const PRIMARY_COVER_USER: ColDef = {
  colId: DealsGridColumnId.PRIMARY_COVER_USER,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    params.data?.primaryCoverUser ? UserHelper.getName(params.data?.primaryCoverUser) : null,
  headerName: Locale.translate(Translation.TC_PRIMARY_COVER),
  width: 150,
  filter: 'agSetColumnFilter'
}

const SECONDARY_COVER_USER: ColDef = {
  colId: DealsGridColumnId.SECONDARY_COVER_USER,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    params.data?.secondaryCoverUser ? UserHelper.getName(params.data?.secondaryCoverUser) : null,
  headerName: Locale.translate(Translation.TC_SECONDARY_COVER),
  width: 150,
  filter: 'agSetColumnFilter'
}

const DEAL_PRE_AGREED: ColDef = {
  colId: DealsGridColumnId.DEAL_PRE_AGREED,
  headerName: Locale.translate(Translation.TC_PRE_AGREED),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.preAgreed,
  valueFormatter: booleanFormatter,
  width: 120,
  filter: 'agSetColumnFilter'
}

const DEAL_MASTER_LEGAL_AGREEMENT: ColDef = {
  colId: DealsGridColumnId.DEAL_MASTER_LEGAL_AGREEMENT,
  headerName: Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENT),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.masterLegalAgreementType,
  width: 180,
  filter: 'agSetColumnFilter',
  hide: true
}

const makeNoteColumn = (index: string) => ({
  colId: `${DealsGridColumnId.NOTES}_${index}`,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.notes?.values[index],
  headerName: Locale.translate(Translation.TC_NOTE, { noteIndex: parseInt(index, 10) + 1 }),
  width: 180,
  filter: 'agSetColumnFilter'
})

const MY_ENTITY_SHORT_NAME: ColDef = {
  colId: DealsGridColumnId.MY_ENTITY_SHORT_NAME,
  headerName: Locale.translate(Translation.TC_MY_ENTITY_SHORT_NAME),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.myEntityShortName,
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const ROE: ColDef = {
  ...BASE_ROE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal?.rightOfExit,
  editable: false,
  hide: true
}

const ROS: ColDef = {
  ...BASE_ROS,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal?.rightOfSubstitution,
  editable: false,
  hide: true
}

const MY_FICC_ENTITY_SHORT_NAME: ColDef = {
  colId: DealsGridColumnId.MY_FICC_ENTITY_SHORT_NAME,
  headerName: Locale.translate(Translation.TC_MY_FICC_ENTITY_SHORT_NAME),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.orderFund?.ficcShortName,
  width: 150,
  filter: 'agSetColumnFilter',
  hide: true
}

const EVERGREEN_NOTICE_PERIOD: ColDef = {
  colId: DealsGridColumnId.EVERGREEN_NOTICE_PERIOD,
  headerName: Locale.translate(Translation.TC_EVERGREEN_NOTICE_PERIOD),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal?.evergreenNoticePeriod,
  valueFormatter: evergreenNoticeFormatter,
  width: 150,
  filter: 'agSetColumnFilter'
}

const ISSUE: ColDef = {
  ...BASE_ISSUE,
  colId: DealsGridColumnId.ISSUE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal?.collateralIssue
}

const MARKET_TRADE_DATE: ColDef = {
  colId: DealsGridColumnId.MARKET_TRADE_DATE,
  headerName: Locale.translate(Translation.TC_MARKET_TRADE_DATE),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.deal?.marketTradeDate,
  width: 150,
  filter: 'agSetColumnFilter',
  valueFormatter: makeDateFormatter()
}

const SUBMITTING_USER: ColDef = {
  colId: DealsGridColumnId.SUBMITTING_USER,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal?.orderSubmitUserId,
  headerName: Locale.translate(Translation.TC_SUBMITTING_USER),
  width: 250,
  filter: 'agSetColumnFilter',
  hide: true
}

const REPO_INTEREST_RATE: ColDef = {
  colId: DealsGridColumnId.DEAL_INTEREST_PERIOD_REPO_INTEREST_RATE,
  headerName: Locale.translate(Translation.TC_PREV_REPO_INTEREST_RATE),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.dealInterestPeriod?.rate,
  width: 150,
  filter: 'agSetColumnFilter',
  valueFormatter: makeRateFormatter()
}

const REPO_INTEREST_START_DATE: ColDef = {
  colId: DealsGridColumnId.DEAL_INTEREST_PERIOD_REPO_INTEREST_START_DATE,
  headerName: Locale.translate(Translation.TC_PREV_REPO_INTEREST_START_DATE),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.dealInterestPeriod?.repoInterestStartDate,
  width: 150,
  filter: 'agSetColumnFilter',
  valueFormatter: makeDateFormatter()
}

const REPO_INTEREST_END_DATE: ColDef = {
  colId: DealsGridColumnId.DEAL_INTEREST_PERIOD_REPO_INTEREST_END_DATE,
  headerName: Locale.translate(Translation.TC_PREV_REPO_INTEREST_END_DATE),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.dealInterestPeriod?.endDate,
  width: 150,
  filter: 'agSetColumnFilter',
  valueFormatter: makeDateFormatter()
}

const REPO_INTEREST_IN_PERIOOD: ColDef = {
  colId: DealsGridColumnId.DEAL_INTEREST_PERIOD_REPO_INTEREST_IN_PERIOD,
  headerName: Locale.translate(Translation.TC_PREV_REPO_INTEREST_IN_PERIOD),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
    const repoInterest = params?.data?.dealInterestPeriod?.repoInterest
    const repoInterestToDate = params?.data?.dealInterestPeriod?.repoInterestToDate

    if (isNil(repoInterest) || isNil(repoInterestToDate)) {
      return null
    }

    return repoInterest - repoInterestToDate
  },
  valueFormatter: makeNumberFormatter({ maxDp: 2 }),
  filterValueGetter: makeNumberFilterGetter({ maxDp: 2 }),
  getQuickFilterText: makeNumberQuickFilterTextGetter({ maxDp: 2 }),
  width: 150,
  filter: 'agSetColumnFilter'
}

const getVisibleColumns = (isAdmin: boolean) => [
  CHECKBOX,
  tradesGridGroupingRowColDef,
  ACTIONS,
  DEAL_STATUS,
  COUNTERPARTY_NAME,
  COLLECTION_NAME,
  COLLATERAL_ISIN_OR_BUI,
  COLLATERAL_NAME,
  COLLATERAL_BASKET_NAME,
  SECONDARY_COLLATERAL_NAME,
  DEAL_DIRECTION,
  DEAL_NOTIONAL,
  PRODUCT,
  PRODUCT_SECTOR,
  DEAL_REPO_TYPE,
  DEAL_STOCK_BORROWING_LENDING_TYPE,
  DEAL_RATE,
  DEAL_FEE,
  DEAL_HAIRCUT_TYPE,
  DEAL_HAIRCUT,
  DEAL_TERM,
  DEAL_START_DATE,
  DEAL_END_DATE,
  MARKET_TRADE_DATE,
  DEAL_START_CONSIDERATION_GROUP,
  DEAL_END_CONSIDERATION,
  FUND_NAME,
  FUND_LEI,
  COUNTERPARTY_CLEARING,
  COUNTERPARTY_SPONSOR,
  EXECUTING_COUNTERPARTY_NAME,
  EXECUTING_COUNTERPARTY_SPONSOR,
  COUNTERPARTY_LEI,
  COUNTERPARTY_TRADER,
  DEAL_SETTLEMENT_VENUE,
  DEAL_CSD,
  COLLATERAL_CURRENCY,
  DEAL_BOND_PRICE,
  DEAL_ALL_IN_PRICE,
  COLLATERAL_ISSUER,
  DEAL_CALLABLE,
  DEAL_CALL_DAYS,
  DEAL_AXE_ROLL_UNWIND,
  DEAL_ID,
  createUTI(isAdmin),
  DEAL_MIC,
  TRADER,
  DEAL_TRADE_DATE,
  DEAL_TRADE_TIME,
  DEAL_UPDATED_BY,
  DEAL_UPDATED_AT_DATE,
  DEAL_UPDATED_AT_TIME,
  DEAL_INVESTMENT_DECISION_MAKER,
  DEAL_EXECUTION_DECISION_MAKER
]

const getHiddenColumns = (isBuySideOrganisation: boolean, isAdmin: boolean) =>
  [
    ...(isBuySideOrganisation || isAdmin ? [CONTAINER_COLLECTION_ID] : []),
    ...(isBuySideOrganisation || isAdmin ? [CONTAINER_COLLECTION_NAME] : []),
    COLLECTION_TYPE,
    COLLATERAL_BASKET_REMAINING_ALLOCATION_TIME,
    COLLATERAL_BASKET_DESCRIPTION,
    COLLATERAL_BASKET_DEAL_ID,
    COLLATERAL_BASKET_DBV_CLASS,
    DEAL_TRADE_TYPE,
    EVERGREEN_NOTICE_PERIOD,
    REFERENCE_RATE_NAME,
    FLOAT_CRYSTALLISATION_DAYS,
    DEAL_REPO_INTEREST,
    BLOCK_TRADE,
    BLOCK_DEAL_ID,
    FUND_BIC,
    CLEARING_VENUE,
    COUNTERPARTY_BIC,
    FUND_FIRM,
    FUND_FIRM_LEI,
    COUNTERPARTY_FIRM,
    COUNTERPARTY_FIRM_LEI,
    COLLATERAL_CUSIP,
    DEAL_CLEAN_PRICE,
    DEAL_INDEX_RATIO,
    DEAL_FACTOR,
    DEAL_ASSET_TYPE,
    DEAL_ACCRUED_INTEREST,
    COLLATERAL_COUPON,
    createBookName(isAdmin),
    createStrategyName(isAdmin),
    PRIME_BROKER,
    BUNDLE_NAME,
    DEAL_ORDER_ID,
    DEAL_AXE_ID,
    DEAL_NEGOTIATION_ID,
    COLLECTION_ID,
    DEAL_ORIGINATOR,
    DEAL_RFQ_CREATED_AT_DATE,
    DEAL_RFQ_CREATED_AT_TIME,
    DEAL_MARKET,
    DEAL_NOTICE_PERIOD_DAYS,
    PRIMARY_COVER_USER,
    SECONDARY_COVER_USER,
    DEAL_PRE_AGREED,
    DEAL_MASTER_LEGAL_AGREEMENT,
    MY_ENTITY_SHORT_NAME,
    ...(isAdmin ? [MY_FICC_ENTITY_SHORT_NAME] : []),
    ISSUE,
    ROE,
    ROS,
    BASE_MARKET_TYPE,
    ...(isBuySideOrganisation ? NOTE_INDEXES.map(makeNoteColumn) : []),
    ...(isBuySideOrganisation ? [SUBMITTING_USER] : [])
  ]
    .map(colDef => ({
      ...colDef,
      hide: true
    }))
    .sort((a, b) => (a.headerName && b.headerName ? a.headerName.localeCompare(b.headerName) : 0))

export const dealsGridColumnDefs = (isAdmin: boolean, isBuySideOrganisation: boolean): (ColGroupDef | ColDef)[] =>
  getVisibleColumns(isAdmin).concat(getHiddenColumns(isBuySideOrganisation, isAdmin))

export const createRelatedDealsGridColumnDefs = (_parentGridId: GridId): (ColGroupDef | ColDef)[] => [
  CHECKBOX,
  tradesGridGroupingRowColDef,
  {
    ...ACTIONS,
    headerComponent: withAllContext(createGroupedActionRenderer(OpenTicketActionsButton)),
    cellRenderer: withAllContext(createActionsRenderer(OpenTicketActionsButton))
  },
  DEAL_STATUS,
  DEAL_ID,
  DEAL_COMMITTED_SERVICING_TYPE,
  DEAL_CREATED_DUE_TO_SERVICING_TYPE,
  COLLATERAL_ISIN_OR_BUI,
  COLLATERAL_NAME,
  COLLATERAL_BASKET_NAME,
  DEAL_NOTIONAL,
  DEAL_START_CONSIDERATION_GROUP,
  DEAL_END_CONSIDERATION,
  DEAL_REPO_INTEREST_START_DATE,
  { ...DEAL_RATE, headerName: Locale.translate(Translation.TC_REPO_RATE) },
  DEAL_HAIRCUT_TYPE,
  DEAL_HAIRCUT,
  DEAL_START_DATE,
  DEAL_END_DATE,
  PARENT_DEAL_ID,
  CHILD_DEAL_ID,
  ORIGINAL_DEAL_ID,
  REPO_INTEREST_RATE,
  REPO_INTEREST_START_DATE,
  REPO_INTEREST_END_DATE,
  DEAL_REPO_INTEREST,
  REPO_INTEREST_IN_PERIOOD,
  DEAL_ACCUMULATED_REPO_INTEREST,
  DEAL_UPDATED_AT_DATE,
  DEAL_UPDATED_AT_TIME,
  DEAL_UPDATED_BY
]

export const dealsGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.deal.dealId,
  rowClassRules: {
    'ag-row--selected-for-related': isSelectedForRelatedDealTrades
  }
}

export const relatedDealsGridOptions: GridOptions<DealDetail> = {
  ...dealsGridOptions,
  getRowId: ({ data }) => `${data.deal.dealId}-${data.deal.version}`
}
